import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Kixz — Teknik SEO & Organik Büyüme Planı (Eylül – Kasım 2026).
 *
 * Canon medya planlarının (CanonBackToSchool2026 / CanonCorals2026) görsel dili ve
 * PDF export mantığı birebir. Fark: para değil trafik planlıyor.
 *
 * TÜM RAKAMLAR CANLI KAYNAKTAN, uydurma DEĞİL:
 *  - GSC (Search Console API, https://kixz.com/) 20 Tem – 18 Ağu 2026, dataState=final
 *  - GA4 (property 521142478) aynı dönem, sessionDefaultChannelGroup kırılımı
 *  - Site taraması: 812 ürün + 208 koleksiyon sayfasının tam HTML denetimi (3–4 Ağu 2026),
 *    ürün sayfası örneği 20 Ağu 2026'da yeniden doğrulandı (hiçbiri düzelmemiş).
 * Varsayım olan tek şey hedef CTR'ler; sayfada ayrıca işaretli.
 */
export default function KixzSeo2026() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const pct = (n, d = 2) => '%' + Number(n).toFixed(d).replace('.', ',');
  // Oturum başına gelir gibi küçük tutarlarda yuvarlama anlamı öldürüyor (₺66,88 → ₺67).
  const fmtCur2 = (n) => '₺' + Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const INK = '#0B1622';       // Kixz siyahı (logodan)
  const PERIOD = '20 Temmuz – 18 Ağustos 2026 (30 gün)';

  // ── GSC · son 30 gün ──────────────────────────────────────────────────────
  const gsc = {
    web: { clicks: 5335, impressions: 34733, ctr: 15.36, position: 7.1 },
    image: { clicks: 209, impressions: 40450, ctr: 0.52, position: 28.8 },
    brand: { queries: 19, clicks: 3278, impressions: 5063, ctr: 64.74 },
    nonbrand: { queries: 1703, clicks: 1366, impressions: 13659, ctr: 10.00 },
  };

  // Sayfa tipi kırılımı (dimension=page; sayfa bazlı toplam, mülk toplamıyla
  // birebir eşleşmez — Google mülk düzeyinde tekilleştirir. Kendi içinde tutarlı.)
  const pageTypes = [
    { key: 'anasayfa', label: 'Ana sayfa', pages: 1, clicks: 4428, impressions: 7949, position: 2.3 },
    { key: 'koleksiyon', label: 'Koleksiyon', pages: 118, clicks: 485, impressions: 24667, position: 5.0 },
    { key: 'urun', label: 'Ürün', pages: 649, clicks: 452, impressions: 24239, position: 6.1 },
    { key: 'kurumsal', label: 'Kurumsal sayfa', pages: 8, clicks: 36, impressions: 12409, position: 3.2 },
    { key: 'blog', label: 'Blog', pages: 5, clicks: 1, impressions: 226, position: 2.3 },
  ].map((r) => ({ ...r, ctr: (r.clicks / r.impressions) * 100 }));

  const catalog = pageTypes.filter((r) => r.key === 'koleksiyon' || r.key === 'urun')
    .reduce((t, r) => ({ clicks: t.clicks + r.clicks, impressions: t.impressions + r.impressions, pages: t.pages + r.pages }), { clicks: 0, impressions: 0, pages: 0 });
  catalog.ctr = (catalog.clicks / catalog.impressions) * 100;
  // Gösterimle ağırlıklı ortalama sıra (koleksiyon 5,0 · ürün 6,1)
  catalog.position = pageTypes
    .filter((r) => r.key === 'koleksiyon' || r.key === 'urun')
    .reduce((s2, r) => s2 + r.position * r.impressions, 0) / catalog.impressions;

  // 1. sırada olup tıklanmayan koleksiyonlar — planın kanıt bloğu
  const topCollections = [
    { url: '/forma', impressions: 4507, clicks: 182, position: 1.3 },
    { url: '/nike-krampon', impressions: 4266, clicks: 39, position: 1.3 },
    { url: '/krampon', impressions: 3826, clicks: 84, position: 1.5 },
    { url: '/giyim', impressions: 1326, clicks: 2, position: 1.2 },
    { url: '/lamine-yamal-kramponlari', impressions: 982, clicks: 4, position: 8.4 },
    { url: '/t-shirt', impressions: 864, clicks: 0, position: 1.5 },
    { url: '/tottenham-formasi', impressions: 761, clicks: 12, position: 8.6 },
    { url: '/arda-guler-kramponlari', impressions: 587, clicks: 21, position: 5.2 },
    { url: '/turkiye-formasi', impressions: 205, clicks: 0, position: 1.7 },
  ].map((r) => ({ ...r, ctr: (r.clicks / r.impressions) * 100 }));

  // ── GA4 · aynı dönem ──────────────────────────────────────────────────────
  const ga4 = {
    organicSessions: 9202,
    organicTx: 105,
    organicRevenue: 615441,
    paidSocialSessions: 49568,
    paidSocialRevenue: 502228,
  };
  const revPerSession = ga4.organicRevenue / ga4.organicSessions;   // ₺66,88
  const organicCvr = (ga4.organicTx / ga4.organicSessions) * 100;   // %1,14
  const aov = ga4.organicRevenue / ga4.organicTx;                   // ₺5.861
  const paidSocialRps = ga4.paidSocialRevenue / ga4.paidSocialSessions;

  // ── Kazanç modeli ─────────────────────────────────────────────────────────
  // Gösterim hacmi SABİT kalsa bile, sadece CTR düzelirse ne oluyor.
  const TARGET_CATALOG_CTR = 4.0;   // varsayım
  const TARGET_IMAGE_CTR = 1.5;     // varsayım
  const model = [
    {
      label: 'Katalog (koleksiyon + ürün)',
      impressions: catalog.impressions,
      nowCtr: catalog.ctr,
      targetCtr: TARGET_CATALOG_CTR,
      note: `${fmtInt(catalog.pages)} sayfa · ort. sıra ${catalog.position.toFixed(1)}`,
    },
    {
      label: 'Görsel arama (tüm site)',
      impressions: gsc.image.impressions,
      nowCtr: gsc.image.ctr,
      targetCtr: TARGET_IMAGE_CTR,
      note: `761 sayfa görselden gösterim alıyor · ort. sıra ${gsc.image.position}`,
    },
  ].map((r) => {
    const nowClicks = Math.round(r.impressions * (r.nowCtr / 100));
    const targetClicks = Math.round(r.impressions * (r.targetCtr / 100));
    return { ...r, nowClicks, targetClicks, delta: targetClicks - nowClicks };
  });
  const deltaClicks = model.reduce((s, r) => s + r.delta, 0);
  const deltaRevenueMonth = deltaClicks * revPerSession;

  // ── Aylık büyüme ──────────────────────────────────────────────────────────
  const monthly = [
    { m: 'Şub', web: 808, img: 9, clicks: 558 },
    { m: 'Mar', web: 1148, img: 26, clicks: 687 },
    { m: 'Nis', web: 4599, img: 144, clicks: 3129 },
    { m: 'May', web: 5300, img: 889, clicks: 2433 },
    { m: 'Haz', web: 20530, img: 9338, clicks: 2929 },
    { m: 'Tem', web: 42799, img: 36777, clicks: 4901 },
    { m: 'Ağu*', web: 19138, img: 24203, clicks: 2661 },
  ];
  const monthlyMax = Math.max(...monthly.map((r) => r.web + r.img));

  // ── Kök sebep kartları ────────────────────────────────────────────────────
  const rootCauses = [
    {
      title: 'Sayfa kimliği yok',
      metric: '0 / 1.020',
      metricLabel: 'sayfada <h1> var',
      body: '812 ürün + 208 koleksiyon sayfasının hiçbirinde h1–h6 yok. Ürün adı hiçbir başlık etiketinde geçmiyor; tek h2/h3 kişiselleştirme widget\'ına ait. Google sayfanın neyi anlattığını başlıktan okuyamıyor.',
      tone: 'red',
    },
    {
      title: 'Görsellerin adı yok',
      metric: pct(97.1, 1),
      metricLabel: 'img alt="Image"',
      body: '55.823 görselin 54.199\'u harfi harfine alt="Image", 1.624\'ünde alt hiç yok. Dosya adları da image_1080.webp. Sayfa başına ~69 görsel içinde hangisinin ürün olduğunu söyleyen tek bir sinyal yok.',
      tone: 'red',
    },
    {
      title: 'Açıklamalar birbirinin kopyası',
      metric: '199 / 208',
      metricLabel: 'koleksiyonda aynı metin',
      body: 'Koleksiyon sayfalarının meta açıklaması 208 sayfada sadece 10 benzersiz metin. 199\'u birebir aynı genel site cümlesi. Ürün tarafında da 90 sayfa 38 grupta tekrar ediyor.',
      tone: 'amber',
    },
  ];

  // ── Yol haritası ──────────────────────────────────────────────────────────
  const PLAN_START = '2026-09-01';
  const PLAN_END = '2026-11-28';
  const phases = [
    {
      id: 'F0',
      name: 'Doğrulama',
      start: '2026-09-01', end: '2026-09-05',
      owner: 'Glowytics',
      color: '#64748b',
      goal: 'Kör düzeltme yapılmaması için önce ölçüm.',
      items: [
        'GSC → URL Denetimi → canlı test → ekran görüntüsü: Google gerçekte hangi görseli basıyor (6 aday: pop-up + 5 mega-menü ürünü)',
        'ikas\'a resmî soru: ProductImage şemasında alt alanı yok — alt metni nereye yazılacak',
        'Tema düzenleyici erişiminin ve tema paketi sürümünün teyidi',
        '1.020 URL için "öncesi" tam taraması arşivlenir (sonrası kıyası için)',
      ],
    },
    {
      id: 'F1',
      name: 'Sayfa kimliği',
      start: '2026-09-08', end: '2026-09-19',
      owner: 'ikas tema kodu',
      color: '#dc2626',
      goal: 'Tıklanma sorununu üreten 5 yapısal hata. Planın etkisinin çoğu burada.',
      items: [
        'Ürün şablonuna <h1>{marka} {ürün adı}</h1> — sadece etiket değişir, tasarım aynı kalır',
        'Koleksiyon şablonuna <h1>{koleksiyon adı}</h1> — AYRI iş, ürün şablonu düzelince otomatik gelmez',
        'Gerçek alt metni: galeri "{marka} {ürün} - görsel {n}", kart ve menü görselleri dahil',
        'Mega-menüdeki 5 "Öne Çıkanlar" ürün kartı kaldırılır (ürünün kendi görselini 17. sıraya itiyor)',
        'joseforma.com hotlink\'i kaldırılır — 812/812 sayfada, ham HTML\'de, Liverpool forması',
        'Bülten pop-up görseli nötr marka görseliyle değiştirilir (ikas paneli · Pazarlama → Popup)',
      ],
    },
    {
      id: 'F2',
      name: 'Snippet & indeks',
      start: '2026-09-22', end: '2026-10-03',
      owner: 'İçerik + tema',
      color: '#ea580c',
      goal: 'Arama sonucunda görünen metni ve indekslenen sayfa sayısını düzeltmek.',
      items: [
        '199 koleksiyona özgün meta açıklaması + CollectionPage.description (202/208 null)',
        'Tekrar eden 90 ürün açıklaması ve 5 ürün başlığına varyant/renk bilgisi',
        'og:image mutlak URL, og:type=product, twitter card, @type "Website" yazım hatası',
        '6 marka koleksiyonuna (nike, adidas, puma, kixz, 90icon, pma-goods) CollectionPage + BreadcrumbList şeması',
        'Sitemap hijyeni: /pages/brands ve /pages/order-tracking 404, ana sayfa sitemap\'te yok, 1 adet 301',
        '/90icon ile /90icon-1 aynı başlıkta — kopya koleksiyon kararı',
      ],
    },
    {
      id: 'F3',
      name: 'Yapısal veri & Merchant',
      start: '2026-10-06', end: '2026-10-17',
      owner: 'Tema + ürün verisi',
      color: '#7c3aed',
      goal: 'Alışveriş yüzeylerinde reddedilme ve eşleşememe riskini kapatmak.',
      items: [
        'Barkodlar mpn yerine gtin12 (413 ürün) / gtin13 (381 ürün) alanına — şu an gtin* alanı 0/808',
        'priceValidUntil boş string, shippingDetails, hasMerchantReturnPolicy, category alanları eklenir',
        '12 üründe barkod hiç yok, 2 üründe geçersiz hane sayısı — panelden girilir',
        '2 boş + 2 çok kısa ürün açıklaması (MC\'de açıklama zorunlu alan)',
        'Store şemasındaki boş PostalAddress ve breadcrumb http @context düzeltmesi',
      ],
    },
    {
      id: 'F4',
      name: 'İçerik & atak mesafesi',
      start: '2026-10-20', end: '2026-11-28',
      owner: 'İçerik',
      color: '#0891b2',
      goal: 'Düzelen altyapının üzerine sıralama kazanmak.',
      items: [
        '4–20. sırada bekleyen 35 marka dışı sorgu için koleksiyon metni + iç link (3.693 gösterim, 55 tık)',
        'Takım/oyuncu koleksiyonları öncelikli: tottenham, fas, arsenal, arjantin, lamine yamal, vini jr',
        '33 tek görselli ve 66 iki görselli ürüne ek fotoğraf',
        'Blog: 15 URL\'nin 4\'ü yazı değil etiket sayfası, 2 slug içerikle uyumsuz',
        'HTML ağırlığı ort. 1.809 KB — tarama bütçesi ve CWV için ayrı teknik çalışma',
      ],
    },
  ];

  // ── İş listesi ────────────────────────────────────────────────────────────
  const IMPACT = { yuksek: 'Yüksek', orta: 'Orta', dusuk: 'Düşük' };
  const tasks = [
    { phase: 'F1', job: 'Ürün şablonuna <h1>', owner: 'Tema kodu', impact: IMPACT.yuksek, effort: 'Düşük', evidence: '0/812 sayfada h1' },
    { phase: 'F1', job: 'Koleksiyon şablonuna <h1>', owner: 'Tema kodu', impact: IMPACT.yuksek, effort: 'Düşük', evidence: '0/208 sayfada h1' },
    { phase: 'F1', job: 'Görsel alt metinleri', owner: 'Tema kodu (+ikas alan teyidi)', impact: IMPACT.yuksek, effort: 'Orta', evidence: '54.199/55.823 img alt="Image"' },
    { phase: 'F1', job: 'Mega-menü ürün kartları', owner: 'Tema kodu', impact: IMPACT.yuksek, effort: 'Düşük', evidence: 'ürün görseli DOM\'da 17. sırada' },
    { phase: 'F1', job: 'joseforma.com hotlink', owner: 'Tema kodu', impact: IMPACT.yuksek, effort: 'Düşük', evidence: '812/812 sayfa, ham HTML' },
    { phase: 'F1', job: 'Bülten pop-up görseli', owner: 'ikas paneli', impact: IMPACT.orta, effort: 'Düşük', evidence: 'forma sayfasında krampon' },
    { phase: 'F2', job: '199 koleksiyon açıklaması', owner: 'İçerik', impact: IMPACT.yuksek, effort: 'Yüksek', evidence: '208 sayfada 10 benzersiz metin' },
    { phase: 'F2', job: 'Tekrar eden desc (90) / title (5)', owner: 'İçerik', impact: IMPACT.orta, effort: 'Orta', evidence: '38 grup tekrar' },
    { phase: 'F2', job: 'og:image mutlak + og:type', owner: 'Tema kodu', impact: IMPACT.orta, effort: 'Düşük', evidence: '809/809 göreli' },
    { phase: 'F2', job: 'Marka koleksiyonlarına şema', owner: 'Tema kodu', impact: IMPACT.orta, effort: 'Düşük', evidence: '6 sayfada şema yok' },
    { phase: 'F2', job: 'Sitemap hijyeni', owner: 'ikas paneli', impact: IMPACT.orta, effort: 'Düşük', evidence: '2 adet 404, ana sayfa eksik' },
    { phase: 'F3', job: 'GTIN doğru alana (795 ürün)', owner: 'Tema kodu', impact: IMPACT.yuksek, effort: 'Orta', evidence: 'gtin* alanı 0/808' },
    { phase: 'F3', job: 'priceValidUntil / shipping / return', owner: 'Tema kodu', impact: IMPACT.orta, effort: 'Orta', evidence: '808/808 eksik' },
    { phase: 'F3', job: '12 eksik + 2 geçersiz barkod', owner: 'ikas paneli', impact: IMPACT.orta, effort: 'Düşük', evidence: 'MC reddi riski' },
    { phase: 'F3', job: '2 boş + 2 kısa açıklama', owner: 'İçerik', impact: IMPACT.dusuk, effort: 'Düşük', evidence: 'MC zorunlu alan' },
    { phase: 'F4', job: '35 sorgu için koleksiyon metni', owner: 'İçerik', impact: IMPACT.yuksek, effort: 'Yüksek', evidence: '3.693 gösterim, 55 tık' },
    { phase: 'F4', job: '33 + 66 ürüne ek fotoğraf', owner: 'İçerik / ürün', impact: IMPACT.orta, effort: 'Orta', evidence: 'katalog ort. 6–7 görsel' },
    { phase: 'F4', job: 'HTML ağırlığı / CWV', owner: 'Tema kodu', impact: IMPACT.orta, effort: 'Yüksek', evidence: 'ort. 1.809 KB' },
  ];

  // ── Ölçüm planı ───────────────────────────────────────────────────────────
  const kpis = [
    { kpi: 'Katalog CTR (koleksiyon + ürün)', source: 'GSC · sayfa kırılımı', now: pct(catalog.ctr), target: pct(TARGET_CATALOG_CTR), freq: 'Haftalık' },
    { kpi: 'Görsel arama CTR', source: 'GSC · type=image', now: pct(gsc.image.ctr), target: pct(TARGET_IMAGE_CTR), freq: 'Haftalık' },
    { kpi: 'Marka dışı tıklama', source: 'GSC · sorgu kırılımı', now: fmtInt(gsc.nonbrand.clicks) + ' / ay', target: '2.400 / ay', freq: 'Aylık' },
    { kpi: 'İndekslenen sayfa', source: 'GSC · kapsam', now: '671 / 1.037', target: '950 / 1.040', freq: 'Aylık' },
    { kpi: 'h1 ve alt kapsaması', source: 'Tam site taraması', now: '0 / 1.020', target: '1.020 / 1.020', freq: 'Faz sonunda' },
    { kpi: 'Organik ciro', source: 'GA4 · Organic Search', now: fmtCur(ga4.organicRevenue) + ' / ay', target: '—', freq: 'Aylık' },
  ];

  const openItems = [
    'Google SERP ekran görüntüsü hâlâ alınmadı. Sitede yanlış görseli üretebilecek 6 aday var; hangisinin bastığı ancak GSC canlı URL testinin ekran görüntüsüyle kesinleşir. Faz 0\'ın ilk maddesi bu.',
    'ikas\'ta ürün görselinin alt alanı YOK (Admin API şema incelemesi: ProductImage = fileName, imageId, isMain, isVideo, order). Tema kodu image?.altText okuyor ama alan hiç gelmiyor. Alt metninin nereye yazılacağı ikas tarafından teyit edilmeli — teyit gelmezse çözüm tema içi özel bileşen olur.',
    'Hedef CTR değerleri (%4,00 katalog, %1,50 görsel) VARSAYIMDIR. Mevcut ortalama sıralamada (5–6) beklenen aralığın alt sınırından seçildi; garanti değil, ölçüm bu hedefe göre yapılacak.',
    'GSC "671 indeksli" sayısının sitemap filtresiyle mi alındığı teyit edilmedi.',
    'access@heyglowy.com (Helen) Kixz markasına erişemiyor, taskboard\'da Kixz panosunu göremiyor. UserBrandAccess kaydı gerekiyor.',
  ];

  // ── Takvim yardımcıları ───────────────────────────────────────────────────
  const TL_START = new Date(PLAN_START);
  const TL_END = new Date(PLAN_END);
  const TL_SPAN = (TL_END - TL_START) / 86400000;
  const tlPct = (d) => (((new Date(d) - TL_START) / 86400000) / TL_SPAN) * 100;
  const fmtDay = (d) => new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  const days = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
  const tlMarks = [
    { label: '1 Eyl', date: '2026-09-01' },
    { label: '1 Eki', date: '2026-10-01' },
    { label: '1 Kas', date: '2026-11-01' },
    { label: '28 Kas', date: PLAN_END },
  ];

  // ── PDF ───────────────────────────────────────────────────────────────────
  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const root = slideRef.current;
      const headerEl = root.querySelector('[data-pdf-header]');
      const sectionEls = Array.from(root.querySelectorAll('[data-pdf-section]'));
      const shoot = (el) => html2canvas(el, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        width: el.offsetWidth,
        height: el.scrollHeight + 12,
        windowWidth: document.documentElement.clientWidth,
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const M = 8;
      const GAP = 6;
      const CW = pw - M * 2;
      const BOTTOM = ph - M - 6;

      const headerCanvas = await shoot(headerEl);
      const headerImg = headerCanvas.toDataURL('image/jpeg', 0.95);
      const headerH = (headerCanvas.height * CW) / headerCanvas.width;
      const TOP_Y = M + headerH + GAP;
      const PAGE_H = BOTTOM - TOP_Y;

      let pages = 0;
      let y = 0;
      const startPage = () => {
        if (pages > 0) pdf.addPage();
        pages += 1;
        pdf.addImage(headerImg, 'JPEG', M, M, CW, headerH);
        y = TOP_Y;
      };

      const slice = (canvas, srcY, srcH) => {
        const c = document.createElement('canvas');
        c.width = canvas.width;
        c.height = srcH;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        return c.toDataURL('image/jpeg', 0.95);
      };

      startPage();
      for (const el of sectionEls) {
        const canvas = await shoot(el);
        const pxPerMm = canvas.width / CW;
        let srcY = 0;
        let remain = canvas.height / pxPerMm;

        while (remain > 0.5) {
          const avail = BOTTOM - y;
          if (remain <= avail) {
            const srcH = Math.round(remain * pxPerMm);
            pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', M, y, CW, remain);
            y += remain + GAP;
            remain = 0;
            break;
          }
          if (remain <= PAGE_H) { startPage(); continue; }
          const srcH = Math.round(avail * pxPerMm);
          pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', M, y, CW, avail);
          srcY += srcH;
          remain -= avail;
          startPage();
        }
      }

      for (let i = 1; i <= pages; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150);
        pdf.text(`Kixz · Teknik SEO & Organik Büyüme Planı · Glowytics · ${i}/${pages}`, pw / 2, ph - M + 2, { align: 'center' });
      }

      pdf.save('Kixz_Teknik_SEO_Plani_2026.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  // ── Küçük bileşenler ──────────────────────────────────────────────────────
  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      amber: 'border-amber-100 bg-amber-50 text-amber-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1 tabular-nums">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  const Pill = ({ children, tone = 'gray' }) => {
    const tones = {
      gray: 'bg-gray-50 text-gray-600 border-gray-200',
      red: 'bg-red-50 text-red-700 border-red-100',
      amber: 'bg-amber-50 text-amber-700 border-amber-100',
      green: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      violet: 'bg-violet-50 text-violet-700 border-violet-100',
      cyan: 'bg-cyan-50 text-cyan-700 border-cyan-100',
    };
    return <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${tones[tone]}`}>{children}</span>;
  };

  const impactTone = (i) => (i === 'Yüksek' ? 'red' : i === 'Orta' ? 'amber' : 'gray');
  const phaseTone = { F0: 'gray', F1: 'red', F2: 'amber', F3: 'violet', F4: 'cyan' };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex justify-end mb-3">
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: INK }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div data-pdf-header className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/kixz/logo.png" alt="Kixz" className="h-10 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">Teknik SEO &amp; Organik Büyüme Planı</p>
              <p className="text-[9px] text-gray-400">kixz.com · Eylül – Kasım 2026 · veri dönemi {PERIOD}</p>
            </div>
          </div>
          <img src="/heyGlowylogo.png" alt="Glowytics" className="h-6 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Durum */}
          <div data-pdf-section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-bold text-gray-800">Durum</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Organik arama, <strong>Kixz'in en çok ciro üreten kanalı</strong>: son 30 günde {fmtInt(ga4.organicSessions)} oturumla
              {' '}<strong>{fmtCur(ga4.organicRevenue)}</strong> ciro. Aynı dönemde Paid Social {fmtInt(ga4.paidSocialSessions)} oturumla
              {' '}{fmtCur(ga4.paidSocialRevenue)} üretti — yani organik oturum başına <strong>{fmtCur2(revPerSession)}</strong>,
              paid social {fmtCur2(paidSocialRps)}.
            </p>
            <p className="text-xs text-gray-600 mt-2 leading-relaxed">
              Buna rağmen sitenin kataloğu aramada <strong>görünüyor ama tıklanmıyor</strong>. {fmtInt(catalog.pages)} koleksiyon ve
              ürün sayfası 30 günde {fmtInt(catalog.impressions)} gösterim alıp yalnızca {fmtInt(catalog.clicks)} tık getiriyor
              ({pct(catalog.ctr)}). Ana sayfa ise 2,3 ortalama sırada {pct(pageTypes[0].ctr)} CTR yapıyor. Sıralama sorunu değil,
              <strong> arama sonucunda tıklanma sorunu</strong> var: 1.020 sayfanın hiçbirinde başlık etiketi yok, görsellerin
              %97'sinin adı <span className="font-mono">"Image"</span>, koleksiyon açıklamalarının 199'u birbirinin kopyası.
            </p>
            <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
              Bu plan üç şeyi yapar: (1) tıklanmayı engelleyen yapısal hataları tema kodunda kapatır, (2) alışveriş
              yüzeylerindeki veri eksiklerini giderir, (3) düzelen altyapının üstüne içerikle sıralama kazanır.
              Tüm rakamlar canlı Search Console ve GA4 verisidir; site bulguları 812 ürün + 208 koleksiyon sayfasının
              tam taramasından gelir ve <strong>20 Ağustos'ta yeniden doğrulanmıştır</strong> (hiçbiri henüz düzeltilmedi).
            </p>
          </div>

          {/* Ana metrikler */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-2">Bugünkü Tablo · {PERIOD}</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Organik Ciro" value={fmtCur(ga4.organicRevenue)} sub={`${fmtInt(ga4.organicTx)} sipariş · GA4`} tone="green" />
              <Stat label="Oturum Başına" value={fmtCur2(revPerSession)} sub={`dönüşüm ${pct(organicCvr)} · sepet ${fmtCur(aov)}`} tone="green" />
              <Stat label="Katalog CTR" value={pct(catalog.ctr)} sub={`${fmtInt(catalog.impressions)} gösterim → ${fmtInt(catalog.clicks)} tık`} tone="red" />
              <Stat label="Görsel Arama CTR" value={pct(gsc.image.ctr)} sub={`${fmtInt(gsc.image.impressions)} gösterim → ${fmtInt(gsc.image.clicks)} tık`} tone="red" />
              <Stat label="Marka Bağımlılığı" value={pct((gsc.brand.clicks / gsc.web.clicks) * 100, 0)} sub={`${gsc.brand.queries} marka sorgusu ${fmtInt(gsc.brand.clicks)} tık`} tone="amber" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Toplam web araması: {fmtInt(gsc.web.clicks)} tık / {fmtInt(gsc.web.impressions)} gösterim · CTR {pct(gsc.web.ctr)} · ort. sıra {gsc.web.position}.
              Marka dışı {fmtInt(gsc.nonbrand.queries)} sorgu {fmtInt(gsc.nonbrand.clicks)} tık getiriyor (CTR {pct(gsc.nonbrand.ctr)}).
            </p>
          </div>

          {/* Kanıt: sıralama var, tık yok */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Sıralama Sorunu Değil, Tıklanma Sorunu</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Aşağıdaki koleksiyonların çoğu zaten <strong>1. sırada</strong>. Buna rağmen 100 gösterimden 1'i bile tıklanmıyor.
              Aynı sitede ana sayfa 2,3. sırada %55,71 CTR yapıyor — yani kullanıcı Kixz'i tıklıyor, ama katalog sayfalarının
              arama sonucunda tıklanacak bir görünümü yok.
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Sayfa</th>
                    <th className="text-right px-3 py-2 font-semibold">Ort. Sıra</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Tık</th>
                    <th className="text-right px-3 py-2 font-semibold">CTR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="bg-emerald-50/60">
                    <td className="px-3 py-2 font-semibold text-emerald-800">/ (ana sayfa) — kıyas</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{pageTypes[0].position}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{fmtInt(pageTypes[0].impressions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{fmtInt(pageTypes[0].clicks)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-800">{pct(pageTypes[0].ctr)}</td>
                  </tr>
                  {topCollections.map((r) => (
                    <tr key={r.url} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-700">{r.url}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">{r.position.toFixed(1)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.clicks)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.ctr < 1 ? 'text-red-600' : 'text-gray-700'}`}>{pct(r.ctr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-3">
              {pageTypes.map((r) => (
                <div key={r.key} className="rounded-xl border border-gray-200 p-3">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase">{r.label}</p>
                  <p className="text-lg font-bold text-gray-800 tabular-nums">{pct(r.ctr)}</p>
                  <p className="text-[9px] text-gray-400 tabular-nums">
                    {fmtInt(r.pages)} sayfa · {fmtInt(r.impressions)} gösterim · sıra {r.position}
                  </p>
                  <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(r.ctr, 60) / 60 * 100}%`, background: r.key === 'anasayfa' ? '#059669' : '#dc2626' }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Sayfa kırılımı Search Console'un <span className="font-mono">page</span> boyutundan alınmıştır; sayfa bazlı toplam,
              mülk düzeyindeki toplamla birebir eşleşmez (Google mülk düzeyinde tekilleştirir). Kendi içinde tutarlıdır,
              kıyaslama bu yüzden aynı boyut içinde yapılmıştır.
            </p>
          </div>

          {/* Büyüme */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Gösterim Hacmi Zaten Büyüyor</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Google siteyi giderek daha çok gösteriyor: web gösterimi Mayıs'ta 5.300 iken Temmuz'da 42.799'a çıktı, görsel
              aramada 889'dan 36.777'ye. Yani sorun görünürlük üretmek değil — <strong>üretilen görünürlüğün tıka
              dönüşmemesi</strong>. Bu, planın etkisini kendiliğinden büyüten bir zemin: aynı düzeltme her ay daha çok
              gösterim üzerinde çalışacak.
            </p>
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-end gap-3 h-40">
                {monthly.map((r) => {
                  const total = r.web + r.img;
                  const h = (total / monthlyMax) * 100;
                  const webShare = (r.web / total) * 100;
                  return (
                    <div key={r.m} className="flex-1 flex flex-col items-center justify-end h-full">
                      <span className="text-[9px] font-bold text-gray-600 tabular-nums mb-1">{fmtInt(total)}</span>
                      <div className="w-full rounded-t-md overflow-hidden flex flex-col justify-end" style={{ height: `${h}%` }}>
                        <div style={{ height: `${100 - webShare}%`, background: '#93c5fd' }} />
                        <div style={{ height: `${webShare}%`, background: INK }} />
                      </div>
                      <span className="text-[9px] text-gray-500 mt-1">{r.m}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                  <span className="w-3 h-2 rounded-sm inline-block" style={{ background: INK }} /> Web araması gösterimi
                </span>
                <span className="flex items-center gap-1.5 text-[10px] text-gray-500">
                  <span className="w-3 h-2 rounded-sm inline-block" style={{ background: '#93c5fd' }} /> Görsel araması gösterimi
                </span>
                <span className="text-[10px] text-gray-400 ml-auto">* Ağustos 1–18 arası, ay tamamlanmadı</span>
              </div>
            </div>
          </div>

          {/* Kök sebep */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Kök Sebep — Üç Yapısal Hata</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Ürün verisi ve JSON-LD sağlam: 809/809 ürün sayfasında Product şeması doğru, Merchant Center görselleri
              808/809 eşleşiyor, aynı ana görseli paylaşan iki ürün bile yok. Sorun veride değil, <strong>sayfa
              yapısında</strong>.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {rootCauses.map((c) => (
                <div key={c.title} className={`rounded-xl border p-4 ${c.tone === 'red' ? 'border-red-100 bg-red-50' : 'border-amber-100 bg-amber-50'}`}>
                  <p className={`text-xs font-bold ${c.tone === 'red' ? 'text-red-800' : 'text-amber-800'}`}>{c.title}</p>
                  <p className={`text-2xl font-bold mt-1 tabular-nums ${c.tone === 'red' ? 'text-red-700' : 'text-amber-700'}`}>{c.metric}</p>
                  <p className={`text-[9px] font-medium ${c.tone === 'red' ? 'text-red-600' : 'text-amber-600'}`}>{c.metricLabel}</p>
                  <p className="text-[10px] text-gray-600 mt-2 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Buna ek olarak her ürün sayfasında ürünün kendi görselinden <strong>önce</strong> 16 görsel var: mega-menüdeki
              5 başka ürün, açılır bülten pop-up'ındaki 3 krampon fotoğrafı ve joseforma.com'dan çekilen bir Liverpool
              forması. Google'ın küçük resmi bunlardan seçme ihtimali, ürünün kendi fotoğrafını seçme ihtimalinden yüksek.
            </p>
          </div>

          {/* Kazanç modeli */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Beklenen Kazanç</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Model tek bir şey varsayıyor: <strong>gösterim hacmi bugünkü seviyede sabit kalsın</strong>, sadece CTR düzelsin.
              Yeni sıralama, yeni içerik, yeni ürün yok. Hedef CTR'ler mevcut ortalama sıranın beklenen aralığının alt
              sınırından seçildi.
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Yüzey</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim / ay</th>
                    <th className="text-right px-3 py-2 font-semibold">Bugün CTR</th>
                    <th className="text-right px-3 py-2 font-semibold">Hedef CTR</th>
                    <th className="text-right px-3 py-2 font-semibold">Bugün Tık</th>
                    <th className="text-right px-3 py-2 font-semibold">Hedef Tık</th>
                    <th className="text-right px-3 py-2 font-semibold">Fark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {model.map((r) => (
                    <tr key={r.label} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <p className="font-semibold text-gray-800">{r.label}</p>
                        <p className="text-[9px] text-gray-400">{r.note}</p>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-600 font-semibold">{pct(r.nowCtr)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">{pct(r.targetCtr)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.nowClicks)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.targetClicks)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">+{fmtInt(r.delta)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={6}>AYLIK EK TIKLAMA</td>
                    <td className="px-3 py-2 text-right tabular-nums">+{fmtInt(deltaClicks)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
              <Stat label="Aylık Ek Tıklama" value={'+' + fmtInt(deltaClicks)} sub="gösterim sabitken" tone="green" />
              <Stat label="Aylık Ek Ciro (tahmini)" value={fmtCur(deltaRevenueMonth)} sub={`${fmtInt(deltaClicks)} × ${fmtCur2(revPerSession)}`} tone="green" />
              <Stat label="Yıllık Karşılığı" value={fmtCur(deltaRevenueMonth * 12)} sub="12 ay sabit varsayımıyla" tone="green" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Ciro çevrimi GA4'ün Organic Search oturum başına gelirinden ({fmtCur2(revPerSession)}) yapılmıştır; arama
              tıklaması ile oturum 1:1 kabul edilmiştir. <strong>Hedef CTR'ler varsayımdır</strong>, taahhüt değildir —
              ölçüm planı bu hedeflere göre haftalık takip edilir. Gösterim hacminin büyümesi (Mayıs→Temmuz 8 kat) modele
              dahil edilmemiştir, yani model muhafazakârdır.
            </p>
          </div>

          {/* Yol haritası — takvim */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Yol Haritası · 1 Eylül – 28 Kasım 2026</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Sıra rastgele değil: önce ölçüm (kör düzeltme yapılmasın), sonra etkisi en yüksek ve eforu en düşük tema
              düzeltmeleri, en sona uzun soluklu içerik işi. Fazlar birbirini beklemek zorunda değil; tek darboğaz
              tema kodu erişimidir.
            </p>
            <div className="rounded-xl border border-gray-200 p-4 overflow-hidden">
              <div className="flex">
                <div className="w-40 md:w-52 shrink-0" />
                <div className="relative flex-1 h-4">
                  {tlMarks.map((m, i) => (
                    <span
                      key={m.label}
                      className="absolute top-0 text-[9px] font-semibold text-gray-400 whitespace-nowrap"
                      style={{
                        left: `${tlPct(m.date)}%`,
                        transform: i === 0 ? 'none' : i === tlMarks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                      }}
                    >
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="relative mt-1.5">
                <div className="absolute inset-0 flex pointer-events-none">
                  <div className="w-40 md:w-52 shrink-0" />
                  <div className="relative flex-1">
                    {tlMarks.map((m) => (
                      <div key={m.label} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: `${tlPct(m.date)}%` }} />
                    ))}
                  </div>
                </div>
                {phases.map((p) => {
                  const left = tlPct(p.start);
                  const width = Math.max(tlPct(p.end) - left, 2);
                  const nearEnd = left > 60;
                  return (
                    <div key={p.id} className="flex items-center h-12 relative">
                      <div className="w-40 md:w-52 shrink-0 pr-3">
                        <p className="text-[11px] font-semibold text-gray-800 whitespace-nowrap" style={{ lineHeight: '1.5' }}>
                          {p.id} · {p.name}
                        </p>
                        <p className="text-[9px] text-gray-400">{p.owner}</p>
                      </div>
                      <div className="relative flex-1 h-12">
                        <div className="absolute top-1/2 -translate-y-1/2" style={{ left: `${left}%`, width: `${width}%` }}>
                          <div className={`absolute bottom-full mb-1 whitespace-nowrap text-[8.5px] font-medium text-gray-500 ${nearEnd ? 'right-0' : 'left-0'}`}>
                            {fmtDay(p.start)} → {fmtDay(p.end)} · {days(p.start, p.end)}g
                          </div>
                          <div className="h-4 rounded-full shadow-sm" style={{ background: p.color }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Faz detayları */}
          {phases.map((p) => (
            <div data-pdf-section key={p.id} className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: p.color }}>
                <div className="flex items-center gap-2">
                  <span className="text-white text-[10px] font-bold bg-white/20 rounded px-1.5 py-0.5">{p.id}</span>
                  <p className="text-white text-sm font-bold">{p.name}</p>
                </div>
                <p className="text-white/80 text-[10px] font-medium">
                  {fmtDay(p.start)} → {fmtDay(p.end)} · {p.owner}
                </p>
              </div>
              <div className="p-4">
                <p className="text-[11px] text-gray-600 font-medium mb-2">{p.goal}</p>
                <ul className="space-y-1.5">
                  {p.items.map((it) => (
                    <li key={it} className="flex gap-2 text-[11px] text-gray-600 leading-relaxed">
                      <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: p.color }} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}

          {/* İş listesi */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">İş Listesi · Etki / Efor / Sahip</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Sahip sütunu kritik: <strong>panelden yapılabilenler sadece</strong> barkodlar, boş/kısa açıklamalar,
              tekrar eden metinler, pop-up görseli ve sitemap kayıtları. Diğer her şey <strong>tema kodu</strong> işidir —
              ikas panelinde karşılığı yoktur.
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Faz</th>
                    <th className="text-left px-3 py-2 font-semibold">İş</th>
                    <th className="text-left px-3 py-2 font-semibold">Sahip</th>
                    <th className="text-left px-3 py-2 font-semibold">Etki</th>
                    <th className="text-left px-3 py-2 font-semibold">Efor</th>
                    <th className="text-left px-3 py-2 font-semibold">Ölçülen kanıt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tasks.map((t) => (
                    <tr key={t.phase + t.job} className="hover:bg-gray-50">
                      <td className="px-3 py-2"><Pill tone={phaseTone[t.phase]}>{t.phase}</Pill></td>
                      <td className="px-3 py-2 font-semibold text-gray-800">{t.job}</td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{t.owner}</td>
                      <td className="px-3 py-2"><Pill tone={impactTone(t.impact)}>{t.impact}</Pill></td>
                      <td className="px-3 py-2 text-gray-500">{t.effort}</td>
                      <td className="px-3 py-2 text-[10px] text-gray-400">{t.evidence}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              ⚠️ h1 ve alt işi <strong>ürün şablonu ve koleksiyon şablonu için ayrı ayrı</strong> verilmelidir; ürün şablonu
              düzelse koleksiyonlar aynı kalır. Tema kodu zaten <span className="font-mono">image?.altText || "Image"</span>
              {' '}okuyor, yani alt alanı beslenirse tema değişikliği gerekmeyebilir — ancak ikas şemasında bu alanın
              bulunmadığı ölçüldü, önce teyit alınmalı.
            </p>
          </div>

          {/* Atak mesafesi */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Atak Mesafesi · 4–20. Sırada Bekleyen Talep</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Son 30 günde 35 marka dışı sorgu ilk sayfanın alt sırasında veya ikinci sayfada: toplam 3.693 gösterim,
              yalnızca 55 tık. Bunların büyük kısmı takım/oyuncu forması ve krampon aramaları; karşılığında zaten bir
              koleksiyon sayfası var, sadece o sayfanın metni ve iç linki yok.
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Sorgu</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Tık</th>
                    <th className="text-right px-3 py-2 font-semibold">Ort. Sıra</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ['tottenham forma', 352, 6, 9.6],
                    ['fas forma', 246, 10, 6.1],
                    ['lamine yamal krampon', 218, 1, 9.3],
                    ['arsenal siyah forma', 186, 0, 7.5],
                    ['fas forması', 174, 4, 7.1],
                    ['arjantin forması 2026', 169, 1, 8.9],
                    ['2014 almanya forması', 147, 0, 9.7],
                    ['isviçre forması', 136, 0, 8.2],
                    ['uruguay forma', 128, 1, 9.2],
                    ['almanya 2014 forma', 115, 2, 8.1],
                    ['fas forması 2026', 106, 7, 4.9],
                    ['barcelona turuncu forma', 104, 0, 6.8],
                  ].map(([q, i, c, p]) => (
                    <tr key={q} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-700">{q}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(i)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(c)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">{p.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold text-gray-800">
                    <td className="px-3 py-2">35 sorgunun tamamı</td>
                    <td className="px-3 py-2 text-right tabular-nums">3.693</td>
                    <td className="px-3 py-2 text-right tabular-nums">55</td>
                    <td className="px-3 py-2 text-right tabular-nums">—</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Ölçüm planı */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Ölçüm Planı</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Her faz sonunda 1.020 URL'nin tam taraması tekrarlanır ve <strong>öncesi/sonrası kıyaslanır</strong> —
              "konsolda hata yok" bir doğrulama değildir. Tarama yöntemi ve ham veri elimizde, tekrar maliyeti düşüktür.
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">KPI</th>
                    <th className="text-left px-3 py-2 font-semibold">Kaynak</th>
                    <th className="text-right px-3 py-2 font-semibold">Bugün</th>
                    <th className="text-right px-3 py-2 font-semibold">Hedef</th>
                    <th className="text-left px-3 py-2 font-semibold">Sıklık</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {kpis.map((k) => (
                    <tr key={k.kpi} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-gray-800">{k.kpi}</td>
                      <td className="px-3 py-2 text-gray-500">{k.source}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-600 font-semibold">{k.now}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">{k.target}</td>
                      <td className="px-3 py-2 text-gray-500">{k.freq}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Açık maddeler */}
          <div data-pdf-section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-bold text-amber-900">Açık Maddeler ve Varsayımlar</p>
            <ul className="mt-2 space-y-2">
              {openItems.map((t) => (
                <li key={t} className="flex gap-2 text-[11px] text-amber-900/90 leading-relaxed">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-500 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-[9px] text-gray-400 text-center pt-2">
            Kaynaklar: Google Search Console API (https://kixz.com/, {PERIOD}, dataState=final) · GA4 property 521142478 ·
            kixz.com tam site taraması (812 ürün + 208 koleksiyon, 3–4 Ağustos 2026; ürün sayfası örneği 20 Ağustos 2026'da
            yeniden doğrulandı) · Merchant Center 5718530755.
          </p>
        </div>
      </div>
    </div>
  );
}
